import React from 'react';
import BaseModal from '../common/BaseModal';

const kwotaPL = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

const SplitPaymentModal = ({
  show,
  totalAmount,
  splitPayments,
  splitPaymentError,
  onUpdateAmount,
  onUpdateCouponCode,
  onAddCouponRow,
  onRemoveCouponRow,
  onCheckCouponRow,
  onConfirm,
  onCancel
}) => {
  const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

  const getTotalSplitAmount = () => {
    return splitPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const totalSplitAmount = getTotalSplitAmount();
  // Blokuj ujemne kwoty w jakiejkolwiek metodzie
  const hasNegativeAmount = splitPayments.some((p) => (parseFloat(p.amount) || 0) < 0);
  // Suma metod musi rownac sie kwocie paragonu z dokladnoscia do 2 miejsc
  const isBalanced = !hasNegativeAmount && round2(totalSplitAmount) === round2(totalAmount);

  // Wiersze kuponow - numerujemy je w kolejnosci (Kupon 1, Kupon 2...)
  const kupony = splitPayments.filter(p => p.method === 'kupon');
  const ostatniKuponId = kupony.length ? kupony[kupony.length - 1].id : null;
  const kluczWiersza = (payment) => payment.id ?? payment.method;

  // "Reszta" per wiersz: wpisz w ten wiersz to, czego jeszcze brakuje do kwoty paragonu
  const handleFillRest = (payment) => {
    const currentAmount = parseFloat(payment.amount) || 0;
    const restAmount = round2(totalAmount - totalSplitAmount + currentAmount);
    onUpdateAmount(kluczWiersza(payment), restAmount < 0 ? 0 : restAmount);
  };

  const footer = (
    <>
      <button
        data-pos-btn="true"
        onClick={onCancel}
        style={{
          flex: 1,
          padding: "6px 12px",
          border: "1px solid var(--border)",
          backgroundColor: "var(--card-bg)",
          color: "var(--text-secondary)",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        Anuluj
      </button>
      <button
        data-pos-btn="true"
        onClick={onConfirm}
        disabled={!isBalanced}
        style={{
          flex: 2,
          padding: "6px 12px",
          border: isBalanced ? "2px solid var(--orange)" : "1px solid var(--border)",
          backgroundColor: isBalanced ? "var(--orange)" : "var(--card-bg)",
          color: isBalanced ? "#fff" : "var(--text-muted)",
          cursor: isBalanced ? "pointer" : "not-allowed",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        Zaplac
      </button>
    </>
  );

  const inputStyle = {
    flex: 1,
    minWidth: 0,
    padding: "0.3rem 0.5rem",
    border: "1px solid var(--border)",
    borderRadius: "4px",
    fontSize: "11px"
  };

  const malyPrzycisk = {
    padding: "4px 10px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-secondary)",
    fontSize: "11px",
    cursor: "pointer",
    whiteSpace: "nowrap"
  };

  return (
    <BaseModal
      isOpen={show}
      onClose={onCancel}
      title="Platnosc dzielona"
      headerIcon={<span style={{ fontSize: "14px" }}>&#128176;</span>}
      headerStyle={{ backgroundColor: "var(--orange)" }}
      size="sm"
      footer={footer}
    >
      {/* Kwota do zaplaty */}
      <div style={{ marginBottom: "0.75rem", padding: "0.5rem", backgroundColor: "var(--bg)", borderRadius: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Do zaplaty:</div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "var(--orange)" }}>
            {totalAmount.toFixed(2)} zl
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Suma:</div>
          <div style={{
            fontSize: "12px",
            fontWeight: "600",
            color: isBalanced ? "var(--success)" : "var(--danger)"
          }}>
            {totalSplitAmount.toFixed(2)} zl
          </div>
        </div>
      </div>

      {/* Metody platnosci */}
      <div style={{ display: "grid", gap: "0.5rem" }}>
        {splitPayments.map((payment) => {
          const jestKupon = payment.method === 'kupon';
          const nrKuponu = jestKupon ? kupony.indexOf(payment) + 1 : 0;
          const etykieta = jestKupon && kupony.length > 1 ? `Kupon ${nrKuponu}` : payment.label;
          const klucz = kluczWiersza(payment);

          return (
            <div key={klucz} data-testid={`split-row-${klucz}`} style={{
              padding: "0.5rem",
              border: "1px solid var(--border)",
              borderRadius: "4px",
              borderLeft: `3px solid ${payment.color}`,
              backgroundColor: "var(--bg)"
            }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "0.4rem" }}>
                <span style={{ marginRight: "0.3rem" }}>
                  {payment.method === 'gotowka' ? '💵' : payment.method === 'karta' ? '💳' : payment.method === 'blik' ? '📱' : '🎟️'}
                </span>
                <span style={{ fontWeight: "600", fontSize: "11px" }}>{etykieta}</span>
                {jestKupon && payment.balance !== null && payment.balance !== undefined && (
                  <span style={{ marginLeft: "auto", fontSize: "10px", color: "var(--success)" }}>
                    saldo {kwotaPL(payment.balance)} zl
                  </span>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                {jestKupon && (
                  <input
                    type="text"
                    placeholder="Kod kuponu"
                    aria-label={`Kod kuponu ${nrKuponu}`}
                    value={payment.couponCode || ''}
                    onChange={(e) => onUpdateCouponCode(klucz, e.target.value)}
                    onBlur={() => {
                      if (onCheckCouponRow && payment.couponCode && payment.couponCode.trim().length >= 4) {
                        onCheckCouponRow(klucz);
                      }
                    }}
                    style={inputStyle}
                  />
                )}

                <input
                  type="number"
                  placeholder="0.00"
                  aria-label={`Kwota ${etykieta}`}
                  min="0"
                  step="0.01"
                  value={payment.amount || ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Blokuj ujemne kwoty na wejsciu
                    const num = parseFloat(raw);
                    onUpdateAmount(klucz, num < 0 ? '0' : raw);
                  }}
                  style={inputStyle}
                />

                <button
                  data-pos-btn="true"
                  onClick={() => handleFillRest(payment)}
                  style={malyPrzycisk}
                  title="Wpisz w ten wiersz reszte do zaplaty"
                >
                  Reszta
                </button>

                {jestKupon && onRemoveCouponRow && (
                  <button
                    data-pos-btn="true"
                    onClick={() => onRemoveCouponRow(klucz)}
                    style={{ ...malyPrzycisk, color: "var(--danger)" }}
                    title={kupony.length > 1 ? "Usun te karte" : "Wyczysc karte"}
                    aria-label={`Usun kupon ${nrKuponu}`}
                  >
                    ✕
                  </button>
                )}
              </div>

              {jestKupon && payment.balanceError && (
                <div style={{ marginTop: "0.3rem", fontSize: "10px", color: "var(--danger)" }}>
                  {payment.balanceError}
                </div>
              )}

              {jestKupon && payment.id === ostatniKuponId && onAddCouponRow && (
                <button
                  data-pos-btn="true"
                  onClick={onAddCouponRow}
                  style={{
                    marginTop: "0.4rem",
                    padding: "4px 10px",
                    border: "1px dashed var(--purple)",
                    backgroundColor: "var(--card-bg)",
                    color: "var(--purple)",
                    fontSize: "11px",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                  title="Klient placi kolejna karta podarunkowa"
                >
                  ➕ Dodaj kolejną kartę
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Blad */}
      {splitPaymentError && (
        <div style={{
          marginTop: "0.5rem",
          padding: "0.4rem",
          backgroundColor: "var(--danger-light)",
          color: "var(--danger)",
          borderRadius: "4px",
          fontSize: "10px"
        }}>
          {splitPaymentError}
        </div>
      )}
    </BaseModal>
  );
};

export default React.memo(SplitPaymentModal);
